/**
 * Centralized error handling and notification system for JKP Factors application.
 *
 * Provides comprehensive error handling including Sentry integration, retry mechanisms,
 * user notifications (toast/modal), and robust AJAX request handling. Replaces
 * scattered error handling patterns with a unified, professional approach.
 *
 * Features:
 * - Automatic retry logic with exponential backoff
 * - Toast notifications for system errors
 * - Bootstrap modal integration for user validation errors
 * - Sentry error tracking integration
 * - Loading state management
 * - Graceful degradation for missing dependencies
 *
 * @author JKP Factors Team
 */

import * as Sentry from "@sentry/browser";

/**
 * Reduce a URL to what an error report needs: no query string, no fragment, and
 * no password-reset token. Sentry is a third party, and these are where a page's
 * address carries secrets and email addresses.
 */
export function scrubUrl(url) {
  if (typeof url !== 'string') return url;

  return url
    .replace(/[?#].*$/, '')
    .replace(/(\/password\/reset\/)[^/]+/, '$1[token]');
}

// Applied to every event before it leaves the browser.
function scrubEvent(event) {
  if (event.request) {
    event.request.url = scrubUrl(event.request.url);
    delete event.request.query_string;

    for (const header of Object.keys(event.request.headers || {})) {
      if (header.toLowerCase() === 'referer') {
        event.request.headers[header] = scrubUrl(event.request.headers[header]);
      }
    }
  }

  if (event.extra) {
    // Only what is known to be safe: callers attach response bodies, request
    // options (headers, payloads) and links, and those stay in the browser.
    const kept = {};
    for (const key of ['status', 'filename', 'lineno', 'colno', 'tags']) {
      if (key in event.extra) kept[key] = event.extra[key];
    }
    if ('url' in event.extra) kept.url = scrubUrl(event.extra.url);
    event.extra = kept;
  }

  return event;
}

// Applied to every breadcrumb (navigation, fetch and XHR records) as it is made.
function scrubBreadcrumb(breadcrumb) {
  for (const key of ['url', 'from', 'to']) {
    if (breadcrumb.data && key in breadcrumb.data) {
      breadcrumb.data[key] = scrubUrl(breadcrumb.data[key]);
    }
  }

  return breadcrumb;
}

// Initialize Sentry with the same DSN as the backend (only if one is configured).
// The layouts render it into a meta tag from SENTRY_DSN at request time, so turning
// Sentry on needs no rebuild. VITE_SENTRY_DSN (build time) remains as a fallback.
const sentryMeta = (name) => document.querySelector(`meta[name="${name}"]`)?.content;
const sentryDsn = sentryMeta('sentry-dsn') || import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn && sentryDsn !== 'undefined') {
  try {
    Sentry.init({
      dsn: sentryDsn,
      environment: sentryMeta('sentry-environment') || import.meta.env.MODE,
      beforeSend: scrubEvent,
      beforeBreadcrumb: scrubBreadcrumb,
      // No console breadcrumbs: they copy whatever was logged, arguments and all,
      // and this file logs each error's full context to the console.
      integrations: [Sentry.breadcrumbsIntegration({ console: false })],
    });
  } catch (error) {
    console.warn('Sentry initialization failed:', error);
  }
}

/**
 * Centralized error handling utility class.
 *
 * Provides methods for user notifications, AJAX retry logic, loading states,
 * and graceful error recovery. Designed to work with Bootstrap modals and
 * jQuery AJAX while providing fallbacks for missing dependencies.
 */
class ErrorHandler {
  /**
   * Initialize the ErrorHandler with default configuration.
   * Sets up retry parameters with environment-specific optimizations.
   */
  constructor() {
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 second
    // Reduce delays in test environment
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      this.retryDelay = 200; // Faster retries for testing
    }
  }

  // Show non-intrusive toast notifications for system errors
  showToast(message, type = 'error', duration = 4000) {
    // Remove any existing toasts
    const existingToasts = document.querySelectorAll('.error-toast');
    existingToasts.forEach(toast => toast.remove());

    const toast = document.createElement('div');
    toast.className = `error-toast alert alert-${type === 'error' ? 'danger' : type} alert-dismissible fade show`;
    // Screen readers announce the message when the toast appears.
    toast.setAttribute('role', 'alert');
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 10000;
      min-width: 320px;
      max-width: 400px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      border: none;
      border-radius: 8px;
      font-size: 14px;
      animation: slideInRight 0.3s ease-out;
      background-color: ${type === 'error' ? 'var(--danger-soft-bg)' : 'var(--info-soft-bg)'} !important;
      color: var(--text) !important;
      padding: 16px !important;
    `;

    // Add the slide-in animation CSS if it doesn't exist
    if (!document.querySelector('#toast-animations')) {
      const style = document.createElement('style');
      style.id = 'toast-animations';
      style.textContent = `
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }

    const icon = type === 'error' ? '⚠️' : 'ℹ️';
    toast.innerHTML = `
      <div class="d-flex align-items-start">
        <span class="me-2">${icon}</span>
        <div class="flex-grow-1">
          <div class="fw-semibold">${type === 'error' ? 'Connection Issue' : 'Notice'}</div>
          <div class="small">${message}</div>
        </div>
        <button type="button" class="btn-close btn-close-sm ms-2" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>
    `;

    document.body.appendChild(toast);

    // Auto-remove with slide-out animation
    setTimeout(() => {
      if (toast.parentNode) {
        toast.style.animation = 'slideOutRight 0.3s ease-in';
        setTimeout(() => {
          if (toast.parentNode) {
            toast.remove();
          }
        }, 300);
      }
    }, duration);

    // Make close button work
    const closeBtn = toast.querySelector('.btn-close');
    closeBtn.addEventListener('click', () => {
      toast.style.animation = 'slideOutRight 0.3s ease-in';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.remove();
        }
      }, 300);
    });
  }

  // Backward compatibility alias for showToast
  showErrorMessage(message, type = 'error') {
    this.showToast(message, type);
  }

  // Show user validation modal (not errors - no Sentry logging)
  showUserModal(title, message) {
    const modal = document.getElementById('userNotificationModal');
    const modalTitle = document.getElementById('userNotificationModalLabel');
    const modalBody = document.getElementById('userNotificationModalBody');

    if (modal && modalTitle && modalBody) {
      modalTitle.textContent = title;
      modalBody.textContent = message;

      // Use Bootstrap 5 modal API
      const bsModal = new bootstrap.Modal(modal);
      bsModal.show();
    }
  }

  // Log error to Sentry and console
  logError(error, context = {}) {
    console.error('Error occurred:', error, context);

    // Only capture real errors, not null/undefined values
    if (error && typeof error === 'object' && (error instanceof Error || error.message || error.stack)) {
      Sentry.captureException(error, {
        tags: {
          section: 'frontend',
          ...context.tags
        },
        extra: context
      });
    } else if (error) {
      // For non-Error objects, say only what kind of thing it was: its contents
      // can be anything, a response body included.
      Sentry.captureMessage(`Non-standard error (${typeof error})`, 'error');
    }
  }

  // Retry mechanism for network requests
  async retryRequest(requestFunction, maxAttempts = this.retryAttempts) {
    let lastError;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const result = await requestFunction();
        return result;
      } catch (error) {
        lastError = error;

        if (attempt === maxAttempts) {
          break;
        }

        // Log retry attempt
        console.warn(`Request failed, attempt ${attempt}/${maxAttempts}. Retrying in ${this.retryDelay}ms...`);

        // Wait before retry with exponential backoff
        await new Promise(resolve =>
          setTimeout(resolve, this.retryDelay * Math.pow(2, attempt - 1))
        );
      }
    }

    throw lastError;
  }

  // Enhanced AJAX request with retry and proper error handling
  async makeRequest(url, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      ...options
    };

    const requestFunction = async () => {
      const response = await fetch(url, defaultOptions);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    };

    try {
      return await this.retryRequest(requestFunction);
    } catch (error) {
      this.logError(error, {
        url,
        options: defaultOptions,
        tags: { type: 'network_request' }
      });

      // Show non-intrusive toast for network errors
      this.showToast(
        'Unable to load data. Please check your connection and try again.',
        'error'
      );

      throw error;
    }
  }

  // jQuery AJAX wrapper with retry
  jQueryAjaxWithRetry(ajaxOptions) {
    return new Promise((resolve, reject) => {
      const makeRequest = (attempt = 1) => {
        $.ajax({
          ...ajaxOptions,
          success: (data) => {
            resolve(data);
          },
          error: (xhr, status, error) => {
            if (attempt < this.retryAttempts) {
              console.warn(`AJAX request failed, attempt ${attempt}/${this.retryAttempts}. Retrying...`);
              setTimeout(() => {
                makeRequest(attempt + 1);
              }, this.retryDelay * Math.pow(2, attempt - 1));
            } else {
              const errorObj = new Error(`AJAX Error: ${status} - ${error}`);
              this.logError(errorObj, {
                url: ajaxOptions.url,
                status: xhr.status,
                responseText: xhr.responseText,
                tags: { type: 'ajax_request' }
              });

              this.showToast(
                'Failed to retrieve data. Please try again later.',
                'error'
              );

              reject(errorObj);
            }
          }
        });
      };

      makeRequest();
    });
  }

  // Handle validation errors with toast notifications
  handleValidationErrors(errors) {
    if (typeof errors === 'object') {
      Object.entries(errors).forEach(([field, messages]) => {
        const fieldMessages = Array.isArray(messages) ? messages : [messages];
        fieldMessages.forEach(message => {
          this.showToast(`${field}: ${message}`, 'warning', 3000);
        });
      });
    } else {
      this.showToast(errors || 'Validation failed', 'warning');
    }
  }

  // Show loading state
  showLoading(elementId = 'loader') {
    const loader = document.getElementById(elementId);
    if (loader) {
      loader.classList.remove('d-none');
    }
  }

  // Hide loading state
  hideLoading(elementId = 'loader') {
    const loader = document.getElementById(elementId);
    if (loader) {
      loader.classList.add('d-none');
    }
  }
}

// Global error handler instance
const errorHandler = new ErrorHandler();
window.errorHandler = errorHandler;

// Global error event listeners
window.addEventListener('error', (event) => {
  // Skip null/undefined errors
  if (event.error === null || event.error === undefined) {
    return;
  }

  window.errorHandler.logError(event.error, {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    tags: { type: 'javascript_error' }
  });
});

window.addEventListener('unhandledrejection', (event) => {
  window.errorHandler.logError(event.reason, {
    tags: { type: 'unhandled_promise_rejection' }
  });
});

export default ErrorHandler;